import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  AdditionalMailingAudience,
  AdditionalMailingType,
  IAdditionalMailing,
} from "src/store/additionalMailing/types";
import {
  ADDITIONAL_MAILING_AUDIENCE_LABEL,
  ADDITIONAL_MAILING_TYPE_LABEL,
} from "src/constants/labels/additionalMailingLabel";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const ISSUER_ADDITIONAL_MAILINGS_COLUMNS = [
  "type",
  "recordDate",
  "dateReceived",
  "solicitor",
  "audience",
  "operationsStatus",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type IssuerAdditionalMailingsColumnData = {
  type: ColumnType;
  recordDate: ColumnType;
  dateReceived: ColumnType;
  solicitor: ColumnType;
  audience: ColumnType;
  operationsStatus: ColumnType;
};

export function getColumns() {
  return ISSUER_ADDITIONAL_MAILINGS_COLUMNS;
}

export function getColumnData({
  getSort,
  onOrderingChange,
}: ColumnDataHelpers): IssuerAdditionalMailingsColumnData {
  return {
    type: {
      sort: getSort("type"),
      sortFn: (data: unknown) => onOrderingChange(data, "type"),
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    recordDate: {
      sort: getSort("proxy_event__record_date"),
      sortFn: (data: unknown) =>
        onOrderingChange(data, "proxy_event__record_date"),
      style: "text",
      title: "Record Date",
    },
    dateReceived: {
      sort: getSort("date_received"),
      sortFn: (data: unknown) => onOrderingChange(data, "date_received"),
      style: "text",
      title: "Date Received",
    },
    solicitor: {
      sort: getSort("solicitor__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "solicitor__name"),
      style: "text",
      title: "Solicitor",
    },
    audience: {
      sort: getSort("audience"),
      sortFn: (data: unknown) => onOrderingChange(data, "audience"),
      style: "text",
      title: "Audience",
    },
    operationsStatus: {
      sort: getSort("operations_status"),
      sortFn: (data: unknown) => onOrderingChange(data, "operations_status"),
      style: "text",
      title: "Status",
    },
  };
}

function formatOperationsStatus(status: string): string {
  if (!status) return "-";
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getRowData(
  events: IAdditionalMailing[],
  onRowClick: (event: IAdditionalMailing) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: IAdditionalMailing;
      idx: string;
    };
    onRowClick(event);
  };

  return events.map((event, idx) => {
    const {
      id,
      type,
      proxyEvent,
      dateReceived,
      solicitor,
      audience,
      operationsStatus,
    } = event;

    return {
      id,
      data: { event, idx },
      rowData: {
        type: {
          content:
            ADDITIONAL_MAILING_TYPE_LABEL[type as AdditionalMailingType] ||
            type,
          clickHandler: handleRowClick,
        },
        recordDate: {
          content: proxyEvent?.recordDate
            ? dayjs(proxyEvent.recordDate).format("MM/DD/YYYY")
            : "-",
          clickHandler: handleRowClick,
        },
        dateReceived: {
          content: dateReceived
            ? dayjs(dateReceived).format("MM/DD/YYYY")
            : "-",
          clickHandler: handleRowClick,
        },
        solicitor: {
          content: solicitor?.name || "-",
          clickHandler: handleRowClick,
        },
        audience: {
          content:
            ADDITIONAL_MAILING_AUDIENCE_LABEL[
              audience as AdditionalMailingAudience
            ] ||
            audience ||
            "-",
          clickHandler: handleRowClick,
        },
        operationsStatus: {
          content: formatOperationsStatus(operationsStatus),
          clickHandler: handleRowClick,
        },
      },
    };
  });
}
